// @ts-nocheck
import React from "react";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import ReactECharts from "echarts-for-react";
import { getTransformerOverview } from "../../api/analytics";
import { InverterCard } from "./cards";
import { PeriodSelector } from "./SiteHierarchy";

const SkeletonCard = () => (
  <div className="h-44 rounded-2xl border border-slate-100 bg-slate-100/80 animate-pulse" />
);

const ErrorBadge = ({ message }) => (
  <div className="flex items-center gap-2 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
    <span className="text-lg">⚠️</span>
    <div>{message}</div>
  </div>
);

export default function TransformerView() {
  const { transformerId } = useParams();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const initialPeriod = searchParams.get("period") || location.state?.period || "day";
  const initialSite = searchParams.get("siteId") || location.state?.siteId || "";
  const arrayId = searchParams.get("arrayId") || location.state?.arrayId || "";
  const [period, setPeriod] = React.useState(initialPeriod);
  const [siteId] = React.useState(initialSite);
  const [transformerName] = React.useState(location.state?.transformerName || `Transformer ${transformerId}`);

  const [inverters, setInverters] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");
  const [trendModal, setTrendModal] = React.useState<{ name: string; series: { x: any; y: number }[]; zLabel: string } | null>(null);

  const loadOverview = React.useCallback(async () => {
    if (!transformerId) return;
    setLoading(true);
    setError("");
      try {
      const payload = await getTransformerOverview(transformerId, period, arrayId);
      const rows = payload?.inverters || payload || [];
      rows.sort((a: any, b: any) => {
        const an = (a.name || a.inverter_name || "").toString().toLowerCase();
        const bn = (b.name || b.inverter_name || "").toString().toLowerCase();
        return an.localeCompare(bn);
      });
      setInverters(rows);
    } catch (err) {
      setError("Unable to load inverter overview.");
      setInverters([]);
    } finally {
      setLoading(false);
    }
  }, [transformerId, period, arrayId]);

  React.useEffect(() => {
    loadOverview();
  }, [loadOverview]);

  const handlePeriodChange = (key: string) => {
    setPeriod(key);
    const params: Record<string, string> = { period: key };
    if (siteId) params.siteId = siteId;
    if (arrayId) params.arrayId = arrayId;
    setSearchParams(params);
  };

  const handleBack = () => {
    const query = new URLSearchParams();
    if (siteId) query.set("siteId", siteId);
    if (period) query.set("period", period);
    navigate(`/dashboard/hierarchy?${query.toString()}`, { replace: true });
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={handleBack}
            className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 shadow-sm hover:border-slate-400"
          >
            ← Back to transformers
          </button>
          <h1 className="text-2xl font-semibold text-slate-900">{transformerName}</h1>
        </div>
        <PeriodSelector value={period} onChange={handlePeriodChange} />
      </div>

      {error && <ErrorBadge message={error} />}

      <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4">
        {loading &&
          Array.from({ length: 6 }).map((_, idx) => (
            <SkeletonCard key={idx} />
          ))}
        {!loading && inverters.length === 0 && (
          <div className="col-span-full rounded-xl border border-slate-200 bg-white p-6 text-center text-slate-500">
            No inverters found for this transformer and period.
          </div>
        )}
        {!loading &&
          inverters.map((inv) => {
            const mapSeries = (arr: any[], fallbackValue?: number) =>
              Array.isArray(arr)
                ? (() => {
                    const mapped = arr
                      .map((p: any, idx: number) => {
                        const tsRaw = p.timestamp ?? p.t ?? p.ts ?? p.time ?? p[0];
                        const val = p.z ?? p[1] ?? null;
                        if (val === null || typeof val !== "number") return null;
                        const ts =
                          tsRaw !== undefined && tsRaw !== null
                            ? new Date(tsRaw).toISOString()
                            : new Date(Date.now() - (arr.length - idx) * 60000).toISOString();
                        return { x: ts, y: val };
                      })
                      .filter(Boolean);
                    if (mapped.length === 0 && typeof fallbackValue === "number") {
                      mapped.push({ x: new Date().toISOString(), y: fallbackValue });
                    }
                    return mapped;
                  })()
                : [];
            const zSeries =
              period === "day"
                ? mapSeries(inv.z_today_series || [], inv.z_median ?? inv.z_live_median ?? inv.z_latest)
                : mapSeries(inv.z_recent || [], inv.z_live_median ?? inv.z_median ?? inv.z_latest);
            const displayZ =
              period === "day"
                ? inv.z_median ?? inv.z_live_median ?? inv.z_latest
                : inv.z_live_median ?? inv.z_median ?? inv.z_latest;
            return (
              <InverterCard
                key={inv.inverter_id || inv.id}
                name={inv.name || inv.inverter_name || `INV-${inv.inverter_id || inv.id}`}
                energyReal={inv.energy_real}
                energyExpected={inv.energy_expected}
                prMedian={inv.pr_median}
                zLiveMedian={displayZ}
                zTodaySeries={mapSeries(inv.z_today_series || inv.z_recent || [], displayZ).map((p) => p.y)}
                trendSeries={zSeries}
                onShowTrend={
                  zSeries.length
                    ? () =>
                        setTrendModal({
                          name: inv.name || inv.inverter_name || `INV-${inv.inverter_id || inv.id}`,
                          series: zSeries,
                          zLabel: period === "day" ? "Today" : period === "week" ? "Last 7 days" : "Last 30 days",
                        })
                    : undefined
                }
                startupStatus={inv.startup_status}
                hasValidPrToday={period === "day" ? inv.has_valid_pr_today : true}
                availability={inv.availability}
                severityDay={
                  inv.severity_day !== undefined
                    ? Number(inv.severity_day) || 0
                    : inv.z_severity === "critical"
                    ? 2
                    : inv.z_severity === "warning"
                    ? 1
                    : 0
                }
                diagnosis={inv.diagnosis_json}
                prLabel={
                  period === "week" ? "PR (week)" : period === "month" ? "PR (month)" : "PR (day)"
                }
              />
            );
          })}
      </div>
      {trendModal && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-slate-900/40 px-4 py-10 backdrop-blur">
          <TrendModal
            data={{
              name: trendModal.name,
              series: trendModal.series.map((p) => ({ day: p.x, z: p.y })),
              zLabel: trendModal.zLabel,
            }}
            onClose={() => setTrendModal(null)}
          />
        </div>
      )}
    </div>
  );
}

const TrendModal = ({ data, onClose }: { data: { name: string; series: { day: string; z: number }[]; zLabel: string }; onClose: () => void }) => {
  const series = Array.isArray(data.series) ? data.series : [];
  const hasData = series.length > 0;
  const zValues = series.map((p) => p.z);
  const options = {
    color: ["#0ea5e9"],
    grid: { top: 24, right: 24, bottom: 72, left: 48 },
    dataZoom: [
      {
        type: "inside",
        xAxisIndex: 0,
        filterMode: "none",
        zoomOnMouseWheel: true,
        moveOnMouseMove: true,
        moveOnMouseWheel: false,
      },
      {
        type: "slider",
        xAxisIndex: 0,
        filterMode: "none",
        height: 16,
        bottom: 24,
      },
    ],
    tooltip: {
      trigger: "axis",
      valueFormatter: (v: number) => Number(v).toFixed(2),
    },
    xAxis: {
      type: "time",
      axisLabel: { color: "#475569" },
      axisLine: { lineStyle: { color: "#cbd5e1" } },
      splitLine: { show: false },
    },
    yAxis: {
      type: "value",
      min: hasData ? Math.min(-3, Math.min(...zValues) - 0.5) : -3,
      max: hasData ? Math.max(3, Math.max(...zValues) + 0.5) : 3,
      axisLabel: { color: "#475569", formatter: (v: number) => v.toFixed(1) },
      splitLine: { lineStyle: { color: "#e2e8f0" } },
    },
    series: [
      {
        name: "Z",
        type: "line",
        smooth: true,
        symbol: "circle",
        symbolSize: 5,
        lineStyle: { width: 2 },
        data: series.map((p) => [p.day, p.z]),
        markLine: {
          symbol: "none",
          silent: true,
          lineStyle: { color: "#94a3b8", type: "dashed" },
          label: {
            formatter: "Zero",
            color: "#fff",
            backgroundColor: "#94a3b8",
            padding: [3, 6],
            borderRadius: 3,
          },
          data: [{ yAxis: 0 }],
        },
      },
    ],
  };

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-slate-900/40 px-4 py-10 backdrop-blur">
      <div className="w-full max-w-3xl rounded-2xl border border-slate-200 bg-white p-4 shadow-xl">
        <div className="mb-2 flex items-center justify-between">
          <div>
            <div className="text-lg font-semibold text-slate-900">{data.name}</div>
            <div className="text-xs text-slate-500">Z trend ({data.zLabel})</div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full border border-slate-200 bg-white px-3 py-1 text-sm font-semibold text-slate-700 hover:border-slate-400"
          >
            Close
          </button>
        </div>
        {hasData ? (
          <ReactECharts option={options as any} style={{ height: 320, width: "100%" }} notMerge lazyUpdate />
        ) : (
          <div className="flex h-40 items-center justify-center text-sm text-slate-500">No deviation data available.</div>
        )}
      </div>
    </div>
  );
};
